import { proxy, useSnapshot } from 'valtio'
import { proxySet } from 'valtio/utils'

import { useLiveDataQuery } from '../../global/blocs/useLiveData'
import { getRole } from '../../global/blocs/roomInfo'

const chatIds = ['chat', 'speaker-chat'] as const
type ChatId = (typeof chatIds)[number]

type ChatInfo = {
  isSelected: boolean
  includeBotGreeting: boolean
  hasReactions: boolean
  isPublic: boolean
}

const chatDefinitions: Record<ChatId, ChatInfo> = {
  chat: {
    isSelected: false,

    includeBotGreeting: true,
    hasReactions: true,
    isPublic: true,
  },
  'speaker-chat': {
    isSelected: false,

    includeBotGreeting: false,
    hasReactions: false,
    isPublic: false,
  },
}

const chatState = proxy<{ selected: ChatId }>({
  selected: 'chat',
})

const chatViewState = proxy({
  isPanelVisible: true,
})

const chatUnreadState = proxy({
  unreadMessageIds: {
    chat: proxySet<number>(),
    'speaker-chat': proxySet<number>(),
  },
})

const isChatId = (id: string): id is ChatId => {
  return (chatIds as readonly string[]).includes(id)
}

const addUnreadMessage = (id: ChatId, messageId: number) => {
  chatUnreadState.unreadMessageIds[id].add(messageId)
}

const removeUnreadMessage = (messageId: number) => {
  chatIds.forEach((id) => {
    chatUnreadState.unreadMessageIds[id].delete(messageId)
  })
}

const clearUnreadMessages = (id: ChatId) => {
  chatUnreadState.unreadMessageIds[id].clear()
}

const useUnreadChatCount = (id: ChatId) => {
  return useSnapshot(chatUnreadState).unreadMessageIds[id].size
}

const useTotalUnreadChatCount = () => {
  const { unreadMessageIds } = useSnapshot(chatUnreadState)
  return unreadMessageIds.chat.size + unreadMessageIds['speaker-chat'].size
}

const setChatPanelVisible = (isVisible: boolean) => {
  if (isVisible) {
    clearUnreadMessages(chatState.selected)
  }
  chatViewState.isPanelVisible = isVisible
}

const shouldMarkMessageUnread = (id: ChatId) => {
  return !chatViewState.isPanelVisible || getSelectedChatId() !== id
}

const getChat = (id: ChatId): ChatInfo => {
  return {
    ...chatDefinitions[id],
    isSelected: id === chatState.selected,
  }
}
const getSelectedChatId = () => chatState.selected
const getSelectedChat = () => getChat(getSelectedChatId())

const setSelectedChat = (newSelected: ChatId) => {
  clearUnreadMessages(newSelected)
  chatState.selected = newSelected
}

const useSelectedChatId = () => useSnapshot(chatState).selected

const useSelectedChat = () => {
  const selectedChatId = useSnapshot(chatState).selected
  return chatDefinitions[selectedChatId]
}

const useSelectedChatKey = () => {
  return useSnapshot(chatState).selected
}

const mapChats = <U>(callbackfn: (value: ChatInfo, index: ChatId) => U) => {
  return chatIds.map((key) => {
    const info = getChat(key)
    return callbackfn(info, key)
  })
}

// TODO: later we should use react query to fetch the live data
// and rely on the cache
const isAudienceChatEnabled = (commentMode: string) => {
  switch (commentMode) {
    case 'all':
    case 'speakers':
      return true
    default:
      return false
  }
}

const useCanInteractWithChat = () => {
  const { data: liveData } = useLiveDataQuery()

  const isOnDemand =
    liveData &&
    liveData.enable_ondemand_p &&
    liveData.live_status === 'previous'

  const selectedChatId = useSelectedChatId()

  if (selectedChatId === 'speaker-chat') return true

  const commentMode = liveData?.comment_mode
  if (!commentMode || commentMode === 'none') return false

  return !isOnDemand && (commentMode === 'all' || getRole() !== 'audience')
}

export {
  type ChatId,
  type ChatInfo,
  chatDefinitions,
  getChat,
  getSelectedChat,
  getSelectedChatId,
  setSelectedChat,
  mapChats,
  setChatPanelVisible,
  shouldMarkMessageUnread,
  useSelectedChatId,
  useSelectedChat,
  useSelectedChatKey,
  isAudienceChatEnabled,
  useCanInteractWithChat,
  isChatId,
  addUnreadMessage,
  removeUnreadMessage,
  clearUnreadMessages,
  useUnreadChatCount,
  useTotalUnreadChatCount,
}
